import { NAMED_PAPERS, type NamedPaper } from '../paper'
import type { Layout } from './plan'

export type SeatingPaper = Extract<NamedPaper, 'letter' | 'tabloid'>
export const SEATING_PAPER_NAMES = [
  'letter',
  'tabloid',
] as const satisfies readonly SeatingPaper[]

export const SEATING_SHOW_OPTIONS = [
  { value: 'name', label: 'Names' },
  { value: 'both', label: 'Names and seat numbers' },
  {
    value: 'number',
    label: 'Seat numbers, with a list',
    description: 'The list goes under the room, or on its own page',
  },
] as const
export type SeatingShowMode = (typeof SEATING_SHOW_OPTIONS)[number]['value']
export const SEATING_SHOW_MODES = SEATING_SHOW_OPTIONS.map(
  ({ value }) => value,
) as [SeatingShowMode, ...SeatingShowMode[]]
export const SEATING_SHOW_DEFAULT: SeatingShowMode = 'name'

export type SeatingPrintOptions = {
  paper: SeatingPaper
  show: Record<string, SeatingShowMode>
}

export const SEATING_PRINT_MARGIN = 0.4
export const SEATING_PRINT_HEAD = 0.9

type PrintableRoom = Pick<Layout, 'roomId' | 'width' | 'height'>

/** Landscape paper dimensions in inches. */
export function seatingPaperSize(paper: SeatingPaper): {
  width: number
  height: number
} {
  const portrait = NAMED_PAPERS[paper]
  return {
    width: portrait.heightMm / 25.4,
    height: portrait.widthMm / 25.4,
  }
}

/** One grid cell in inches, with the whole room held inside the page. */
export function seatingCellFor(
  room: Pick<PrintableRoom, 'width' | 'height'>,
  paper: SeatingPaper,
): number {
  const sheet = seatingPaperSize(paper)
  return Math.min(
    (sheet.width - 2 * SEATING_PRINT_MARGIN) / room.width,
    (sheet.height - 2 * SEATING_PRINT_MARGIN - SEATING_PRINT_HEAD) /
      room.height,
  )
}

function pick<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T,
): T {
  return allowed.includes(value as T) ? (value as T) : fallback
}

/** Parse the choices carried by the PDF address. */
export function parseSeatingPrintOptions(
  query: Record<string, unknown>,
  rooms: PrintableRoom[],
): SeatingPrintOptions {
  const paper = pick(query.paper, SEATING_PAPER_NAMES, 'letter')
  return {
    paper,
    show: Object.fromEntries(
      rooms.map((room) => [
        room.roomId,
        pick(
          query[`show_${room.roomId}`],
          SEATING_SHOW_MODES,
          SEATING_SHOW_DEFAULT,
        ),
      ]),
    ),
  }
}
